import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Users, Trash2, Activity, Calendar, UserCheck, ArrowLeft, Home, RotateCcw, Clock, CheckCircle, XCircle, FileText, Bell, Mail, MessageSquare, CalendarClock, MapPin, Phone, CreditCard, DollarSign, TrendingUp, ExternalLink, RefreshCw, Lock, Unlock, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import BabiDocLogo from '../components/BabiDocLogo';
import UserAvatar from '../components/UserAvatar';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ patients: [], doctors: [], admins: [], pending_doctors: [], locked_accounts: [] });
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState(null);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [resettingStats, setResettingStats] = useState(false);
  const [processingDoctor, setProcessingDoctor] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [unlockingAccount, setUnlockingAccount] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes, appointmentsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/appointments/upcoming`),
        axios.get(`${API}/admin/transactions/summary`).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setUpcomingAppointments(appointmentsRes.data.appointments || []);
      setTransactionSummary(summaryRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (filter = 'all') => {
    setLoadingTransactions(true);
    try {
      let params = {};
      
      // Apply date filter
      const now = new Date();
      if (filter === 'today') {
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        params.created_after = startOfDay.toISOString();
      } else if (filter === 'week') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - 7);
        params.created_after = startOfWeek.toISOString();
      } else if (filter === 'month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        params.created_after = startOfMonth.toISOString();
      }
      
      const response = await axios.get(`${API}/admin/transactions`, { params });
      setTransactions(response.data.transactions || []);
      setTransactionStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Erreur lors du chargement des transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleTransactionFilterChange = (value) => {
    setTransactionFilter(value);
    fetchTransactions(value);
  };

  const resetCounters = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir remettre à zéro les compteurs de visites et de connexions ?')) {
      return;
    }

    setResettingStats(true);
    try {
      await axios.post(`${API}/stats/reset`);
      toast.success('Compteurs remis à zéro avec succès');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la remise à zéro');
    } finally {
      setResettingStats(false);
    }
  };

  const approveDoctor = async (doctorId, doctorName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir approuver le Dr. ${doctorName} ?`)) {
      return;
    }

    setProcessingDoctor(doctorId);
    try {
      await axios.put(`${API}/admin/doctors/${doctorId}/approve`);
      toast.success(`Dr. ${doctorName} a été approuvé avec succès`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
    } finally {
      setProcessingDoctor(null);
    }
  };

  const rejectDoctor = async (doctorId, doctorName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir rejeter le Dr. ${doctorName} ? Cette action enverra un email de notification.`)) {
      return;
    }

    setProcessingDoctor(doctorId);
    try {
      await axios.put(`${API}/admin/doctors/${doctorId}/reject`);
      toast.success(`Dr. ${doctorName} a été rejeté`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setProcessingDoctor(null);
    }
  };

  const deleteUser = async (userId, userName, userRole) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${userName} (${userRole}) ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Utilisateur supprimé avec succès');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const unlockAccount = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir déverrouiller le compte de ${userName} ?`)) {
      return;
    }

    setUnlockingAccount(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/unlock`);
      toast.success(`Le compte de ${userName} a été déverrouillé. Un email de notification a été envoyé.`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du déverrouillage');
    } finally {
      setUnlockingAccount(null);
    }
  };

  const sendReminder = async (appointmentId, target) => {
    setSendingReminder(`${appointmentId}-${target}`);
    try {
      const response = await axios.post(`${API}/admin/appointments/${appointmentId}/remind?target=${target}`);
      const details = response.data.details;
      
      let message = 'Rappels envoyés: ';
      if (details.email_sent?.length > 0) {
        message += `Email (${details.email_sent.join(', ')}) `;
      }
      if (details.whatsapp_sent?.length > 0) {
        message += `WhatsApp (${details.whatsapp_sent.join(', ')})`;
      }
      if (details.errors?.length > 0) {
        message += ` | Erreurs: ${details.errors.join(', ')}`;
      }
      
      toast.success(message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du rappel');
    } finally {
      setSendingReminder(null);
    }
  };

  const formatAppointmentDate = (dateStr) => {
    if (!dateStr) return 'Date inconnue';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-1 text-teal-200 hover:text-white transition-colors"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                <Home className="w-4 h-4" />
              </button>
              <img 
                src="https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png" 
                alt="Côte d'Ivoire" 
                className="w-12 h-12 object-cover rounded-full"
              />
              <div>
                <h1 className="text-xl font-bold">BabiDoc - Administration</h1>
                <p className="text-xs text-teal-100">Panneau de contrôle</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                data-testid="profile-link"
              >
                <UserAvatar name={user?.full_name} role={user?.role} size="sm" />
                <span className="font-medium hidden sm:inline">{user?.full_name}</span>
              </button>
              <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-teal-700" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
              <Card className="bg-white border-l-4 border-l-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Utilisateurs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-gray-900">{stats.users.total}</div>
                    <Users className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-orange-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Patients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-gray-900">{stats.users.patients}</div>
                    <User className="w-8 h-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-green-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Médecins</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-gray-900">{stats.users.doctors}</div>
                    <UserCheck className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-amber-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">En attente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-gray-900">{users.counts?.pending_doctors || 0}</div>
                    <Clock className="w-8 h-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-purple-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Rendez-vous</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-gray-900">{stats.appointments.total}</div>
                    <Calendar className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Revenus (ce mois)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-xl font-bold text-gray-900">
                      {transactionSummary?.monthly?.revenue_formatted || '0 FCFA'}
                    </div>
                    <DollarSign className="w-8 h-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Visits & Connections Stats with Reset Button */}
            <Card className="mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-700">
                    <Activity className="w-5 h-5 inline mr-2 text-teal-600" />
                    Statistiques du site
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={resetCounters}
                    disabled={resettingStats}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="reset-counters-btn"
                  >
                    <RotateCcw className={`w-4 h-4 mr-2 ${resettingStats ? 'animate-spin' : ''}`} />
                    {resettingStats ? 'Remise à zéro...' : 'Remettre à zéro'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Visites</p>
                    <p className="text-2xl font-bold text-teal-600">{stats.site_stats?.visits || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Connexions</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.site_stats?.connections || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Inscriptions</p>
                    <p className="text-2xl font-bold text-green-600">{stats.site_stats?.registrations || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Pending Doctors Validation Section */}
        {users.pending_doctors && users.pending_doctors.length > 0 && (
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-amber-800 flex items-center">
                <Clock className="w-6 h-6 mr-2" />
                Médecins en attente de validation ({users.pending_doctors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.pending_doctors.map((doctor) => (
                  <div 
                    key={doctor.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white rounded-lg border border-amber-200 shadow-sm"
                    data-testid={`pending-doctor-${doctor.id}`}
                  >
                    <div className="flex-1 mb-3 sm:mb-0">
                      <div className="flex items-center space-x-3">
                        <UserAvatar name={doctor.full_name} role="doctor" size="md" />
                        <div>
                          <p className="font-semibold text-gray-900">Dr. {doctor.full_name}</p>
                          <p className="text-sm text-gray-500">{doctor.email}</p>
                        </div>
                      </div>
                      <div className="mt-2 ml-13 pl-13 flex flex-wrap gap-2 text-sm">
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded">
                          <FileText className="w-3 h-3 mr-1" />
                          N° Ordre: <strong className="ml-1">{doctor.order_number || 'Non renseigné'}</strong>
                        </span>
                        {doctor.phone_number && (
                          <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded">
                            Tel: {doctor.phone_number}
                          </span>
                        )}
                        {doctor.address && (
                          <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded">
                            {doctor.address}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => approveDoctor(doctor.id, doctor.full_name)}
                        disabled={processingDoctor === doctor.id}
                        data-testid={`approve-doctor-${doctor.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {processingDoctor === doctor.id ? '...' : 'Approuver'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => rejectDoctor(doctor.id, doctor.full_name)}
                        disabled={processingDoctor === doctor.id}
                        data-testid={`reject-doctor-${doctor.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        {processingDoctor === doctor.id ? '...' : 'Rejeter'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Locked Accounts Section */}
        {users.locked_accounts && users.locked_accounts.length > 0 && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-red-800 flex items-center">
                <Lock className="w-6 h-6 mr-2" />
                Comptes verrouillés ({users.locked_accounts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.locked_accounts.map((account) => (
                  <div 
                    key={account.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white rounded-lg border border-red-200 shadow-sm"
                    data-testid={`locked-account-${account.id}`}
                  >
                    <div className="flex-1 mb-3 sm:mb-0">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{account.full_name}</p>
                          <p className="text-sm text-gray-500">{account.email}</p>
                        </div>
                      </div>
                      <div className="mt-2 ml-13 pl-13 flex flex-wrap gap-2 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded ${
                          account.role === 'doctor' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {account.role === 'doctor' ? 'Médecin' : 'Patient'}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 rounded">
                          {account.failed_login_attempts || 5} tentatives échouées
                        </span>
                        {account.locked_at && (
                          <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-gray-600">
                            Verrouillé le {new Date(account.locked_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => unlockAccount(account.id, account.full_name)}
                      disabled={unlockingAccount === account.id}
                      data-testid={`unlock-account-${account.id}`}
                    >
                      {unlockingAccount === account.id ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Unlock className="w-4 h-4 mr-2" />
                      )}
                      Déverrouiller
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Appointments Section */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-700 flex items-center">
                <CalendarClock className="w-6 h-6 mr-2 text-blue-600" />
                Rendez-vous à venir ({upcomingAppointments.length})
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchDashboardData}
                className="text-teal-600 border-teal-300 hover:bg-teal-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {upcomingAppointments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucun rendez-vous à venir</p>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingAppointments.map((apt) => (
                  <div 
                    key={apt.id} 
                    className="p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`appointment-${apt.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Appointment Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            apt.status === 'confirmed' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {apt.status === 'confirmed' ? 'Confirmé' : 'En attente'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatAppointmentDate(apt.appointment_date)} à {apt.time_slot}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                          {/* Patient Info */}
                          <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                            <UserAvatar name={apt.patient?.full_name} role="patient" size="sm" />
                            <div>
                              <p className="text-xs text-orange-600 font-medium">Patient</p>
                              <p className="font-semibold text-gray-900">{apt.patient?.full_name}</p>
                              <div className="text-sm text-gray-500 space-y-1">
                                <p className="flex items-center">
                                  <Mail className="w-3 h-3 mr-1" />
                                  {apt.patient?.email || 'Non renseigné'}
                                </p>
                                <p className="flex items-center">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {apt.patient?.phone_number || 'Non renseigné'}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Doctor Info */}
                          <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                            <UserAvatar name={apt.doctor?.clinic_name} role="doctor" size="sm" />
                            <div>
                              <p className="text-xs text-green-600 font-medium">Médecin</p>
                              <p className="font-semibold text-gray-900">{apt.doctor?.clinic_name}</p>
                              <div className="text-sm text-gray-500 space-y-1">
                                <p className="flex items-center">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {apt.doctor?.specialization || 'Non spécifié'}
                                </p>
                                <p className="flex items-center">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {apt.doctor?.city || 'Non renseignée'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {apt.reason && (
                          <p className="mt-2 text-sm text-gray-600">
                            <strong>Motif:</strong> {apt.reason}
                          </p>
                        )}
                      </div>
                      
                      {/* Reminder Buttons */}
                      <div className="flex flex-col gap-2 lg:w-auto">
                        <p className="text-xs text-gray-500 font-medium mb-1">Envoyer un rappel à:</p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50 w-full"
                          onClick={() => sendReminder(apt.id, 'patient')}
                          disabled={sendingReminder === `${apt.id}-patient`}
                          data-testid={`remind-patient-${apt.id}`}
                        >
                          {sendingReminder === `${apt.id}-patient` ? (
                            <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <Bell className="w-4 h-4 mr-2" />
                          )}
                          Patient
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50 w-full"
                          onClick={() => sendReminder(apt.id, 'doctor')}
                          disabled={sendingReminder === `${apt.id}-doctor`}
                          data-testid={`remind-doctor-${apt.id}`}
                        >
                          {sendingReminder === `${apt.id}-doctor` ? (
                            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <Bell className="w-4 h-4 mr-2" />
                          )}
                          Médecin
                        </Button>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                          onClick={() => sendReminder(apt.id, 'both')}
                          disabled={sendingReminder === `${apt.id}-both`}
                          data-testid={`remind-both-${apt.id}`}
                        >
                          {sendingReminder === `${apt.id}-both` ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-1" />
                              <MessageSquare className="w-4 h-4 mr-1" />
                            </>
                          )}
                          Les deux
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Gestion des Utilisateurs & Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs 
              defaultValue="patients" 
              className="w-full"
              onValueChange={(value) => {
                if (value === 'transactions' && transactions.length === 0) {
                  fetchTransactions();
                }
              }}
            >
              <TabsList className="mb-6">
                <TabsTrigger value="patients" data-testid="patients-tab">
                  Patients ({users.patients?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="doctors" data-testid="doctors-tab">
                  Médecins ({users.doctors?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="admins" data-testid="admins-tab">
                  Administrateurs ({users.admins?.length || 0})
                </TabsTrigger>
                <TabsTrigger 
                  value="transactions" 
                  data-testid="transactions-tab"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Transactions
                </TabsTrigger>
              </TabsList>

              {/* Patients Tab */}
              <TabsContent value="patients">
                {users.patients?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    Aucun patient enregistré
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.patients?.map((patient) => (
                      <div key={patient.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <UserAvatar name={patient.full_name} role="patient" size="md" />
                            <div>
                              <p className="font-semibold text-gray-900">{patient.full_name}</p>
                              <p className="text-sm text-gray-500">{patient.email}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full">Patient</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteUser(patient.id, patient.full_name, 'patient')}
                            data-testid={`delete-patient-${patient.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Doctors Tab */}
              <TabsContent value="doctors">
                {users.doctors?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    Aucun médecin enregistré
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.doctors?.map((doctor) => (
                      <div key={doctor.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <UserAvatar name={doctor.full_name} role="doctor" size="md" />
                            <div>
                              <p className="font-semibold text-gray-900">{doctor.full_name}</p>
                              <p className="text-sm text-gray-500">{doctor.email}</p>
                              {doctor.order_number && (
                                <p className="text-xs text-gray-400">N° Ordre: {doctor.order_number}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">Médecin</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteUser(doctor.id, doctor.full_name, 'médecin')}
                            data-testid={`delete-doctor-${doctor.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Admins Tab */}
              <TabsContent value="admins">
                <div className="space-y-3">
                  {users.admins?.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <UserAvatar name={admin.full_name} role="admin" size="md" />
                          <div>
                            <p className="font-semibold text-gray-900">{admin.full_name}</p>
                            <p className="text-sm text-gray-500">{admin.email}</p>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full">Administrateur</span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Transactions Tab */}
              <TabsContent value="transactions">
                <div className="space-y-6">
                  {/* Filters and Stats */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <Select value={transactionFilter} onValueChange={handleTransactionFilterChange}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Filtrer par période" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes les transactions</SelectItem>
                          <SelectItem value="today">Aujourd'hui</SelectItem>
                          <SelectItem value="week">Cette semaine</SelectItem>
                          <SelectItem value="month">Ce mois</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => fetchTransactions(transactionFilter)}
                        disabled={loadingTransactions}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingTransactions ? 'animate-spin' : ''}`} />
                        Actualiser
                      </Button>
                    </div>
                    
                    {transactionStats && (
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-green-700 font-medium">{transactionStats.succeeded} réussies</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg">
                          <Clock className="w-4 h-4 text-yellow-600" />
                          <span className="text-yellow-700 font-medium">{transactionStats.pending} en attente</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-700 font-bold">{transactionStats.total_revenue_formatted}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Transactions List */}
                  {loadingTransactions ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Aucune transaction trouvée</p>
                      <p className="text-sm mt-2">Les paiements Stripe apparaîtront ici</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((tx) => (
                        <div 
                          key={tx.id} 
                          className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                          data-testid={`transaction-${tx.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {/* Status Badge */}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                tx.status === 'succeeded' ? 'bg-green-100 text-green-700' :
                                tx.status === 'canceled' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {tx.status_label}
                              </span>
                              {tx.refunded && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  Remboursé
                                </span>
                              )}
                              <span className="text-sm text-gray-500">{tx.created_at_formatted}</span>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              {/* Amount */}
                              <div className="text-xl font-bold text-gray-900">
                                {tx.amount_formatted}
                              </div>
                              
                              {/* Payment Method */}
                              {tx.payment_method && (
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                  <CreditCard className="w-4 h-4" />
                                  <span className="capitalize">{tx.payment_method.brand}</span>
                                  <span>•••• {tx.payment_method.last4}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Doctor Info */}
                            {tx.doctor_info ? (
                              <div className="mt-2 flex items-center gap-2">
                                <UserAvatar name={tx.doctor_info.name} role="doctor" size="xs" />
                                <div className="text-sm">
                                  <span className="font-medium text-gray-700">{tx.doctor_info.name}</span>
                                  {tx.doctor_info.clinic && (
                                    <span className="text-gray-500"> - {tx.doctor_info.clinic}</span>
                                  )}
                                </div>
                              </div>
                            ) : tx.customer_email && (
                              <div className="mt-2 text-sm text-gray-500">
                                <Mail className="w-3 h-3 inline mr-1" />
                                {tx.customer_email}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-3 md:mt-0">
                            <span className="text-xs text-gray-400 font-mono">{tx.id.slice(0, 15)}...</span>
                            {tx.receipt_url && (
                              <a 
                                href={tx.receipt_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Reçu
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
